import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Popover,
    Heading,
} from 'react-aria-components';
import { useState, useEffect,useCallback, useMemo } from 'react';
import { parseDate, getLocalTimeZone, DateFormatter,today } from '@internationalized/date';
import { I18nProvider } from 'react-aria';
import "./DatePickerBot.css";
const handleResizeObserverError = (error) => {
  if (error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    // Ignore this specific error as it's generally harmless
    return;
  }
  console.error('ResizeObserver error:', error);
};

// Add error listener
if (typeof window !== 'undefined') {
  window.addEventListener('error', handleResizeObserverError);
}

const DatePickerBot = ({ entry, handleInputChange }) => {
  // Safely parse initial date from entry.startDate (MM-DD-YYYY format)
  const parseInitialDate = (dateString) => {
    if (!dateString) {
      return null;
    }
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parts[2];
      const month = parts[0];
      const day = parts[1];
      const isoFormatted = `${year}-${month}-${day}`;
      try {
        return parseDate(isoFormatted);
      } catch (e) {
        console.error(`DatePickerBot: Error parsing initial date '${dateString}' (converted to '${isoFormatted}'):`, e);
        return null;
      }
    } else {
      console.warn(`DatePickerBot: Initial date string has unexpected format: ${dateString}. Expected MM-DD-YYYY.`);
      return null;
    }
  };

    const [dateValue, setDateValue] = useState(() => parseInitialDate(entry.startDate));
    const [mode, setMode] = useState('day');
    const [visibleYear, setVisibleYear] = useState(dateValue?.year || today(getLocalTimeZone()).year);
    const [visibleMonth, setVisibleMonth] = useState(dateValue?.month || today(getLocalTimeZone()).month);

    const getLastDayOfMonth = (year, month) => {
      return new Date(year, month, 0).getDate();
    };

  const onDateChange = (newDate) => {
    if (!newDate) {
      setDateValue(null);
      handleInputChange(entry.id, 'startDate', '');
      return;
    }

    const year = newDate.year;
    const month = newDate.month;
    const day = newDate.day;

    if (year < 1000 || year > 9999) {
      setDateValue(newDate);
      return;
    }

    let adjustedDate = newDate;
    const lastDayCurrentMonth = getLastDayOfMonth(year, month);

    if (day > lastDayCurrentMonth) {
      adjustedDate = newDate.set({ day: lastDayCurrentMonth });
    }

    setDateValue(adjustedDate);

    try {
      const formatter = new DateFormatter('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        calendar: 'gregory',
      });
      const formattedAsMMDDYYYYSlash = adjustedDate ? formatter.format(adjustedDate.toDate(getLocalTimeZone())) : '';
      const formattedForFormData = formattedAsMMDDYYYYSlash.replace(/\//g, '-');
      handleInputChange(entry.id, 'startDate', formattedForFormData);
    } catch (error) {
      console.error(`DatePickerBot: Error formatting date:`, error);
    }
  };

  const datesAreEqual = (date1, date2) => {
    if (date1 === null && date2 === null) return true;
    if (date1 === null || date2 === null) return false;

    if (typeof date1.equals === 'function' && typeof date2.equals === 'function') {
      return date1.equals(date2);
    }

    if (date1.toString && date2.toString) {
      return date1.toString() === date2.toString();
    }

    return date1 === date2;
  };

  useEffect(() => {
    const newParsedDate = parseInitialDate(entry.startDate);

    if (newParsedDate === null && dateValue === null) {
      return;
    }

    if (newParsedDate === null && dateValue !== null) {
      setDateValue(null);
      return;
    }

    if (newParsedDate !== null && dateValue === null) {
      setDateValue(newParsedDate);
      return;
    }

    if (newParsedDate !== null && dateValue !== null && !datesAreEqual(newParsedDate, dateValue)) {
      setDateValue(newParsedDate);
      return;
    }
  }, [entry.startDate]);

  const renderYearGrid = useCallback(() => {
    const start = Math.max(1926, visibleYear - 12);
    const years = [];

    for (let y = start; y <= Math.min(2026, start + 23); y++) {
      years.push(
        <div
          key={y}
          className={`year-cell ${y === visibleYear ? 'selected' : ''}`}
          onClick={() => {
            setVisibleYear(y);
            setMode('day');
          }}
        >
          {y}
        </div>
      );
    }

    return <div className="year-grid">{years}</div>;
    }, [visibleYear]);
    const renderMonthGrid = useCallback(() => {
        const months = Array.from({ length: 12 }, (_, i) =>
          new Date(0, i).toLocaleString('en-US', { month: 'short' })
        );

      return (
        <div className="month-grid">
          {months.map((monthName, index) => (
            <div
              key={index}
              className={`month-cell ${index + 1 === visibleMonth ? 'selected' : ''}`}
              onClick={() => {
                setVisibleMonth(index + 1);
                setMode('day');
              }}
            >
              {monthName}
            </div>
          ))}
        </div>
      );
    }, [visibleMonth]);
  const handlePrevNavigation = useCallback(() => {
        if (mode === 'year') {
          setVisibleYear(y => Math.max(1926, y - 12));
        } else if (mode === 'day') {
          if (visibleMonth === 1) {
            if (visibleYear > 1926) {
              setVisibleMonth(12);
              setVisibleYear(y => y - 1);
            }
          } else {
            setVisibleMonth(m => m - 1);
          }
        } else if (mode === 'month') {
          setVisibleYear(y => Math.max(1926, y - 1));
        }
      }, [mode, visibleMonth]);
      const handleNextNavigation = useCallback(() => {
            if (mode === 'year') {
              setVisibleYear(y => Math.min(2026, y + 12));
            } else if (mode === 'day') {
              if (visibleMonth === 12) {
                if (visibleYear < 2026) {
                  setVisibleMonth(1);
                  setVisibleYear(y => y + 1);
                }
              } else {
                setVisibleMonth(m => m + 1);
              }
            } else if (mode === 'month') {
              setVisibleYear(y => Math.min(2026, y + 1));
            }
          }, [mode, visibleMonth]);
       const calendarTitle = useMemo(() => {
            if (mode === 'year') {
              return `${Math.max(1926, visibleYear - 12)} - ${Math.min(2026, visibleYear + 11)}`;
            } else {
              return (
                <span>
                  <span
                    onClick={() => setMode('month')}
                    style={{ cursor: 'pointer', marginRight: '4px', textDecoration: 'underline' }}
                  >
                    {new Date(visibleYear, visibleMonth - 1).toLocaleString('en-US', { month: 'long' })}
                  </span>
                  <span
                    onClick={() => setMode('year')}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {visibleYear}
                  </span>
                </span>
              );
            }
          }, [mode, visibleYear, visibleMonth]);
        const focusedValue = useMemo(() => {
          return parseDate(`${visibleYear}-${String(visibleMonth).padStart(2, '0')}-01`);
        }, [visibleYear, visibleMonth]);
  return (
    <I18nProvider locale="en-US">
      <div className="date-picker-container">
        <DatePicker
          value={dateValue}
          onChange={onDateChange}
          minValue={parseDate('1940-04-04')}
          maxValue={parseDate('2040-04-04')}
          className="react-aria-DatePicker"
          granularity="day"
          shouldForceLeadingZeros={true}
          isDisabled={entry.isPrefilled && !entry.isEditing}
        >
          <Group
            className="react-aria-GroupBot"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #242929',
              borderRadius: '4px',
              padding: '4px 8px',
              width: '100%',
              boxSizing: 'border-box',
              fontSize: '14px', // Mimic Material-UI small size
            }}
          >
            <DateInput className="react-aria-DateInput">
              {(segment) => (
                <DateSegment
                  segment={segment}
                  className="react-aria-DateSegment"
                  style={{
                    minWidth: segment.type === 'month' || segment.type === 'day' ? '2ch' : segment.type === 'year' ? '4ch' : 'auto',
                    textAlign: 'center',
                  }}
                />
              )}
            </DateInput>
            <Button className="react-aria-Button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Button>
          </Group>
         <Popover className="react-aria-Popover">
            <Dialog className="react-aria-Dialog">
              <div className="calendar-header">
                <Button onPress={handlePrevNavigation}>◀</Button>
                <Heading className="calendar-title">
                  {calendarTitle}
                </Heading>
                <Button onPress={handleNextNavigation}>▶</Button>
              </div>

              {mode === 'year' && renderYearGrid()}
              {mode === 'month' && renderMonthGrid()}

              {mode === 'day' && (
                <Calendar focusedValue={focusedValue}>
                  <CalendarGrid className="react-aria-CalendarGrid">
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className="react-aria-CalendarCell"
                        // onPress={() => handleDateSelect(date)}
                      />
                    )}
                  </CalendarGrid>
                </Calendar>
              )}
            </Dialog>
          </Popover>
        </DatePicker>
      </div>
    </I18nProvider>
  );
};

export default DatePickerBot;